<template>
  <div class="shortcuts-section">
    <div class="shortcuts-container">
      <template v-if="dashboardStore.isEditMode">
        <!-- Edit Mode -->
        <button
          v-for="(link, index) in dashboardStore.getLinks()"
          :key="`${index}-${link.url}`"
          type="button"
          class="shortcut-card editable"
          @click="openEditModal(index)"
        >
          <button
            type="button"
            class="delete-btn"
            @click.stop="dashboardStore.removeLink(index)"
          >
            ×
          </button>
          <div class="shortcut-icon">
            <i :class="link.icon"></i>
          </div>
          <span class="shortcut-text">{{ link.name }}</span>
        </button>
        <button
          type="button"
          class="shortcut-card add-card"
          @click="openEditModal(null)"
        >
          <div class="shortcut-icon">
            <i class="fas fa-plus"></i>
          </div>
          <span class="shortcut-text">新規追加</span>
        </button>
      </template>
      <template v-else>
        <!-- View Mode -->
        <a
          v-for="(link, index) in dashboardStore.getLinks()"
          :key="`${index}-${link.url}`"
          :href="link.url"
          target="_blank"
          rel="noopener noreferrer"
          class="shortcut-card"
          :aria-label="`${link.name} (${index + 1})`"
          :data-index="index"
          :data-shortcut-number="index + 1"
        >
          <div class="shortcut-icon">
            <i :class="link.icon"></i>
          </div>
          <span class="shortcut-text">{{ link.name }}</span>
        </a>
        <div
          v-if="dashboardStore.getLinks().length === 0"
          class="empty-message"
        >
          ショートカットを追加してください
        </div>
      </template>
    </div>
  </div>

  <!-- Edit Modal -->
  <ShortcutModal
    v-if="isModalOpen"
    :link="editingLink"
    :index="editingIndex"
    @save="handleSaveLink"
    @close="closeModal"
  />
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useDashboardStore } from "@/stores/dashboard";
import type { ShortcutLink } from "@/types/models";
import ShortcutModal from "./ShortcutModal.vue";

const dashboardStore = useDashboardStore();
const isModalOpen = ref(false);
const editingIndex = ref<number | null>(null);
const editingLink = ref<ShortcutLink | null>(null);

const openEditModal = (index: number | null): void => {
  if (index === null) {
    editingIndex.value = null;
    editingLink.value = null;
  } else {
    editingIndex.value = index;
    editingLink.value = { ...dashboardStore.getLinks()[index] };
  }
  isModalOpen.value = true;
};

const closeModal = (): void => {
  isModalOpen.value = false;
  editingIndex.value = null;
  editingLink.value = null;
};

const handleSaveLink = (link: ShortcutLink): void => {
  if (editingIndex.value === null) {
    dashboardStore.addLink(link);
  } else {
    dashboardStore.updateLink(editingIndex.value, link);
  }
  closeModal();
};
</script>

<style scoped>
.shortcuts-section {
  width: 100%;
}

.shortcuts-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 80px));
  gap: 12px;
  width: 100%;
  justify-content: center;
}

.shortcut-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  text-decoration: none;
  color: #202124;
  transition: all 0.2s;
  position: relative;
  cursor: pointer;
  aspect-ratio: 1;
}

.shortcut-card:hover {
  background: #f5f5f5;
  border-color: #d0d0d0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.shortcut-card.editable {
  border: 2px dashed #1f73e6;
}

.shortcut-card.editable:hover {
  background: #e8f0ff;
}

.shortcut-card.add-card {
  border: 2px dashed #b0b0b0;
  color: #5f6368;
}

.shortcut-card.add-card:hover {
  border-color: #8a8a8a;
}

.delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #d33b27;
  color: white;
  border: none;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.delete-btn:hover {
  background: #c2281a;
  transform: scale(1.1);
}

.shortcut-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 24px;
  color: #1f73e6;
}

.shortcut-text {
  font-size: 11px;
  text-align: center;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-message {
  grid-column: 1 / -1;
  text-align: center;
  color: #999;
  padding: 20px;
}
</style>
